import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/providers";
import { useSeo } from "@/lib/seo";
import { Star } from "@/components/site/Doodles";

const Shell = ({ title, sub, children, testid }) => (
  <div className="relative min-h-[80vh] grid place-items-center px-4 py-16 grid-paper" data-testid={testid}>
    <div className="relative w-full max-w-md">
      <Star className="absolute -top-8 -left-6 w-14 h-14 z-10" />
      <div className="rounded-[2rem] bg-white sticker p-8 sm:p-10">
        <h1 className="font-display text-4xl text-ink mb-1">{title}</h1>
        {sub && <p className="text-sm text-soft mt-4 mb-6">{sub}</p>}
        {children}
      </div>
    </div>
  </div>
);

const Field = ({ label, testid, ...props }) => (
  <label className="block space-y-1.5">
    <span className="text-sm text-soft">{label}</span>
    <input {...props} data-testid={testid} className="kh-input" />
  </label>
);

const Submit = ({ busy, children, testid }) => (
  <button disabled={busy} data-testid={testid} className="btn-pop btn-primary w-full py-3.5 rounded-full font-bold disabled:opacity-60 flex items-center justify-center gap-2">
    {busy && <Loader2 className="animate-spin" size={18} />}{children}
  </button>
);

const ErrorBox = ({ msg }) => msg ? <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2.5" data-testid="auth-error">{msg}</p> : null;

const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return { busy, error, run };
};

export function Login() {
  useSeo("تسجيل الدخول");
  const [f, setF] = useState({ email: "", password: "" });
  const { busy, error, run } = useSubmit();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const submit = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/login", f);
    setUser(data);
    nav(sp.get("next") || "/dashboard");
  }); };
  return (
    <Shell title="أهلاً بيك تاني" sub="سجّل دخولك وكمّل من مكان ما وقفت." testid="login-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="البريد الإلكتروني" type="email" required dir="ltr" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="login-email-input" />
        <Field label="كلمة المرور" type="password" required dir="ltr" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="login-password-input" />
        <ErrorBox msg={error} />
        <Submit busy={busy} testid="login-submit-btn">دخول</Submit>
        <div className="flex justify-between text-sm pt-2">
          <Link to="/forgot-password" data-testid="login-forgot-link" className="text-soft hover:text-brand">نسيت كلمة المرور؟</Link>
          <Link to={`/register${sp.get("next") ? `?next=${sp.get("next")}` : ""}`} data-testid="login-register-link" className="text-brand font-semibold hover:underline">حساب جديد</Link>
        </div>
      </form>
    </Shell>
  );
}

export function Register() {
  useSeo("إنشاء حساب");
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const { busy, error, run } = useSubmit();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const submit = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/register", f);
    setUser(data);
    nav(sp.get("next") || "/dashboard");
  }); };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title="ابدأ رحلتك" sub="اعمل حسابك في أقل من دقيقة." testid="register-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="الاسم بالكامل" required minLength={2} value={f.name} onChange={set("name")} testid="register-name-input" />
        <Field label="البريد الإلكتروني" type="email" required dir="ltr" value={f.email} onChange={set("email")} testid="register-email-input" />
        <Field label="رقم الموبايل (اختياري)" dir="ltr" value={f.phone} onChange={set("phone")} testid="register-phone-input" />
        <Field label="كلمة المرور (8 حروف على الأقل)" type="password" required minLength={8} dir="ltr" value={f.password} onChange={set("password")} testid="register-password-input" />
        <ErrorBox msg={error} />
        <Submit busy={busy} testid="register-submit-btn">إنشاء الحساب</Submit>
        <p className="text-xs text-soft">بالتسجيل أنت موافق على <Link to="/terms" className="underline">الشروط</Link> و<Link to="/privacy" className="underline">سياسة الخصوصية</Link>.</p>
        <p className="text-sm text-center text-soft">عندك حساب؟ <Link to="/login" data-testid="register-login-link" className="text-brand font-semibold">سجّل دخول</Link></p>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  useSeo("نسيت كلمة المرور");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit();
  const submit = (e) => { e.preventDefault(); run(async () => { await api.post("/auth/forgot-password", { email }); setSent(true); }); };
  return (
    <Shell title="نسيت كلمة المرور؟" sub="اكتب بريدك وهنبعتلك رابط لإعادة تعيينها." testid="forgot-page">
      {sent ? (
        <p className="text-ink bg-purple-50 rounded-xl p-4 text-sm leading-relaxed" data-testid="forgot-success">لو البريد مسجل عندنا، هيوصلك رابط إعادة تعيين كلمة المرور خلال دقائق. افحص صندوق الرسائل غير المرغوب فيها كمان.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="البريد الإلكتروني" type="email" required dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} testid="forgot-email-input" />
          <ErrorBox msg={error} />
          <Submit busy={busy} testid="forgot-submit-btn">إرسال الرابط</Submit>
        </form>
      )}
      <Link to="/login" className="block text-center text-sm text-soft mt-5 hover:text-brand">رجوع لتسجيل الدخول</Link>
    </Shell>
  );
}

export function ResetPassword() {
  useSeo("إعادة تعيين كلمة المرور");
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [ok, setOk] = useState(false);
  const { busy, error, run } = useSubmit();
  const submit = (e) => { e.preventDefault(); run(async () => { await api.post("/auth/reset-password", { token: sp.get("token") || "", password: pw }); setOk(true); }); };
  return (
    <Shell title="كلمة مرور جديدة" testid="reset-page">
      {ok ? (
        <div className="space-y-4 mt-6" data-testid="reset-success">
          <p className="text-ink">تم تغيير كلمة المرور بنجاح.</p>
          <Link to="/login" className="btn-pop btn-primary block text-center py-3 rounded-full font-bold">تسجيل الدخول</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 mt-6">
          <Field label="كلمة المرور الجديدة (8 حروف على الأقل)" type="password" required minLength={8} dir="ltr" value={pw} onChange={(e) => setPw(e.target.value)} testid="reset-password-input" />
          <ErrorBox msg={error} />
          <Submit busy={busy} testid="reset-submit-btn">حفظ</Submit>
        </form>
      )}
    </Shell>
  );
}
